import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./HomePage.css";
import API_BASE_URL from "./RestAPI.js";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { formatDate } from "./Common Components/CommonFunction.js";
import {
  FiActivity, FiAlertTriangle, FiCheckCircle, FiClock,
  FiTrendingUp, FiTrendingDown, FiUsers, FiZap,
  FiChevronDown, FiRefreshCw, FiTarget, FiAlertCircle, FiThumbsUp, FiThumbsDown
} from "react-icons/fi";
import { GoBug } from "react-icons/go";
import FilterDropdown from "./Common Components/FilterDropdown";

/* ───── Color palette ───── */
const COLORS = {
  green: '#4caf50', blue: '#2196f3', orange: '#ff9800', red: '#f44336',
  purple: '#7c5cfc', teal: '#00bcd4', pink: '#e91e63', amber: '#ffc107',
  lime: '#8bc34a', indigo: '#3f51b5', cyan: '#00acc1', grey: '#78909c',
};

const STATUS_COLORS = {
  'To Do': COLORS.blue, 'Dev In Progress': COLORS.orange, 'Dev Completed': COLORS.teal,
  'In Review': COLORS.indigo, 'QA In Progress': COLORS.amber, 'QA Pass': COLORS.green,
  'QA Fail': COLORS.red, 'Done': COLORS.green, 'Closed': COLORS.grey, 'Live': COLORS.green, 'Moved to Production': COLORS.green, 'Production': COLORS.green,
};

const getStatusColor = (status) => STATUS_COLORS[status] || COLORS.grey;

/* ───── Utility: classify status into phases ───── */
const classifyStatus = (status) => {
  const s = (status || '').toLowerCase();
  if (/done|completed|live|production/.test(s)) return 'done';
  if (/progress|dev|qa|review/.test(s)) return 'inProgress';
  if (/to.do|todo|open|new/.test(s)) return 'todo';
  if (/fail|reject|block/.test(s)) return 'blocked';
  return 'other';
};

/* ═══════════════ Main Component ═══════════════ */
const HomePage = () => {
  const [sprintsList, setSprintList] = useState([]);
  const [selectedSprintIdx, setSelectedSprintIdx] = useState(0);
  const [analysisData, setAnalysisData] = useState(null);
  const [qaData, setQaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  };

  /* ───── Fetch sprint overview ───── */
  const fetchSprints = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}reports/sprint/overview/`, { method: "GET", headers });
      const data = await res.json();
      if (data.status === "success" && data.data?.length > 0) {
        setSprintList(data.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  /* ───── Fetch sprint analysis ───── */
  const fetchAnalysis = useCallback(async (sprintId) => {
    try {
      const res = await fetch(`${API_BASE_URL}reports/sprint/analysis/?sprint_id=${sprintId}`, { method: "GET", headers });
      const data = await res.json();
      if (data.status === "success") setAnalysisData(data.data || []);
      else setAnalysisData([]);
    } catch { setAnalysisData([]); }
  }, []);

  /* ───── Fetch QA fails ───── */
  const fetchQAData = useCallback(async (sprintId) => {
    try {
      const res = await fetch(`${API_BASE_URL}reports/qa/fails/detail/?sprint_id=${sprintId}`, { method: "GET", headers });
      const data = await res.json();
      if (data.status === "success") setQaData(data);
      else setQaData(null);
    } catch { setQaData(null); }
  }, []);

  useEffect(() => { fetchSprints(); }, []);

  const sprint = sprintsList[selectedSprintIdx];

  useEffect(() => {
    if (sprint?.id) {
      setAnalysisData(null);
      setQaData(null);
      fetchAnalysis(sprint.id);
      fetchQAData(sprint.id);
    }
  }, [sprint?.id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSprints();
    if (sprint?.id) {
      await Promise.all([fetchAnalysis(sprint.id), fetchQAData(sprint.id)]);
    }
    setRefreshing(false);
  };

  /* ───── Computed metrics ───── */
  const metrics = useMemo(() => {
    if (!sprint?.status_log) return null;
    const log = sprint.status_log;
    const total = sprint.total_tickets || 0;
    const entries = Object.entries(log);

    let done = 0, inProg = 0, todo = 0, blocked = 0;
    entries.forEach(([status, count]) => {
      const cls = classifyStatus(status);
      if (cls === 'done') done += count;
      else if (cls === 'inProgress') inProg += count;
      else if (cls === 'todo') todo += count;
      else if (cls === 'blocked') blocked += count;
    });

    const completionPct = total > 0 ? Math.round((done / total) * 100) : 0;
    const bugCount = log['QA Fail'] || log['QA Failed'] || blocked;
    // Bug-typed tickets are tracked separately from the Story/Epic total
    const bugTickets = sprint.bug_count || 0;

    // Sprint health: weighted score (completion is key, bugs hurt, stuck tickets hurt)
    const stuckRatio = total > 0 ? todo / total : 0;
    const bugRatio = total > 0 ? bugCount / total : 0;
    const healthScore = Math.max(0, Math.min(100,
      Math.round(completionPct * 0.6 + (1 - stuckRatio) * 25 + (1 - bugRatio) * 15)
    ));

    // Velocity (done tickets)
    const velocity = done;

    return { total, done, inProg, todo, blocked, completionPct, bugCount, bugTickets, healthScore, velocity, entries };
  }, [sprint]);

  /* ───── Anomalies & Insights ───── */
  const insights = useMemo(() => {
    if (!metrics || !sprint) return { good: [], bad: [] };
    const good = [];
    const bad = [];
    const { total, done, inProg, todo, blocked, completionPct, bugCount } = metrics;

    // Good signals
    if (completionPct >= 80) good.push('Sprint is on track with ' + completionPct + '% completion');
    if (completionPct >= 50 && completionPct < 80) good.push('Good progress at ' + completionPct + '% done');
    if (blocked === 0 && bugCount === 0) good.push('No blockers or QA failures detected');
    if (done > 0 && todo === 0) good.push('All tickets have been picked up — no idle work');
    if (inProg > 0 && inProg <= Math.ceil(total * 0.4)) good.push('Work in progress is well-distributed');

    // Bad signals / anomalies
    if (todo > total * 0.5) bad.push(`${todo} of ${total} tickets still in To Do — sprint might be understaffed`);
    if (bugCount > 0) bad.push(`${bugCount} ticket(s) failed QA — needs attention`);
    if (blocked > total * 0.2) bad.push(`High blockage rate: ${blocked} tickets blocked`);
    if (completionPct < 30 && total > 0) bad.push('Completion is critically low at ' + completionPct + '%');
    if (inProg > total * 0.6) bad.push('Too many tickets in progress — possible context switching');
    if (done === 0 && total > 0) bad.push('No tickets completed yet');

    // Dev workload anomalies from analysis data (Story + Epic only — bugs are separate)
    if (analysisData && Array.isArray(analysisData) && analysisData.length > 0) {
      const devTimes = {};
      analysisData.filter(t => t.ticket_type !== 'Bug').forEach(t => {
        const devs = t.developer_names || t.developers || '';
        const devName = typeof devs === 'string' ? devs.split(',')[0]?.trim() : (devs[0]?.name || devs[0] || '');
        if (devName && t.dev_time_taken) {
          devTimes[devName] = (devTimes[devName] || 0) + parseFloat(t.dev_time_taken || 0);
        }
      });
      const times = Object.values(devTimes);
      if (times.length > 1) {
        const avg = times.reduce((a, b) => a + b, 0) / times.length;
        const max = Math.max(...times);
        const maxDev = Object.entries(devTimes).find(([, v]) => v === max)?.[0];
        if (max > avg * 2 && maxDev) {
          bad.push(`${maxDev} has significantly higher workload (${max.toFixed(1)}h vs avg ${avg.toFixed(1)}h)`);
        }
      }
    }

    if (good.length === 0) good.push('Keep pushing — progress will come');
    if (bad.length === 0) bad.push('No anomalies detected — sprint looks healthy');

    return { good, bad };
  }, [metrics, analysisData, sprint]);

  /* ───── Status pie data ───── */
  const pieData = useMemo(() => {
    if (!metrics?.entries) return [];
    return metrics.entries.map(([label, value], i) => ({
      id: i, value, label, color: getStatusColor(label),
    }));
  }, [metrics]);

  /* ───── Story + Epic only — bugs are surfaced separately via qaSummary ───── */
  const storyAnalysisData = useMemo(() => {
    if (!Array.isArray(analysisData)) return [];
    return analysisData.filter(t => t.ticket_type !== 'Bug');
  }, [analysisData]);

  /* ───── Developer workload bar data ───── */
  const devWorkload = useMemo(() => {
    if (!storyAnalysisData.length) return null;
    const devMap = {};
    storyAnalysisData.forEach(t => {
      const devs = t.developer_names || t.developers || '';
      const devName = typeof devs === 'string' ? devs.split(',')[0]?.trim() : (devs[0]?.name || devs[0] || '');
      if (devName) {
        if (!devMap[devName]) devMap[devName] = { tickets: 0, devHrs: 0, qaHrs: 0 };
        devMap[devName].tickets += 1;
        devMap[devName].devHrs += parseFloat(t.dev_time_taken || 0);
        devMap[devName].qaHrs += parseFloat(t.qa_time_taken || 0);
      }
    });
    const names = Object.keys(devMap);
    if (names.length === 0) return null;
    return {
      names,
      tickets: names.map(n => devMap[n].tickets),
      devHrs: names.map(n => Math.round(devMap[n].devHrs * 10) / 10),
      qaHrs: names.map(n => Math.round(devMap[n].qaHrs * 10) / 10),
    };
  }, [storyAnalysisData]);

  /* ───── QA summary ───── */
  const qaSummary = useMemo(() => {
    if (!qaData?.data || !Array.isArray(qaData.data)) return null;
    let totalBugs = 0, completedBugs = 0, pendingBugs = 0;
    qaData.data.forEach(t => {
      totalBugs += (t.bug_count || 0);
      completedBugs += (t.bug_completed_count || 0);
      pendingBugs += (t.bug_pending_count || 0);
    });
    return {
      stories: qaData.total_count || qaData.data.length,
      totalBugs, completedBugs, pendingBugs,
      passRate: totalBugs > 0 ? Math.round((completedBugs / totalBugs) * 100) : 100,
    };
  }, [qaData]);

  /* ───── Developer Performance Scoreboard ───── */
  const devPerformance = useMemo(() => {
    if (!storyAnalysisData.length) return null;

    const devStats = {};

    storyAnalysisData.forEach(t => {
      const devs = t.developer_names || t.developers || '';
      const devName = typeof devs === 'string' ? devs.split(',')[0]?.trim() : (devs[0]?.name || devs[0] || '');
      if (!devName) return;

      if (!devStats[devName]) devStats[devName] = { tickets: 0, done: 0, inProg: 0, todo: 0, bugsRaised: 0, bugsResolved: 0, devHrs: 0, qaHrs: 0, estHrs: 0 };
      devStats[devName].tickets += 1;
      devStats[devName].devHrs += parseFloat(t.dev_time_taken || t.dev_time_took || 0);
      devStats[devName].qaHrs += parseFloat(t.qa_time_taken || t.qa_time_took || 0);
      devStats[devName].estHrs += parseFloat(t.dev_est_time || 0);

      const status = (t.ticket_status || t.status_name || '').toLowerCase();
      if (/done|completed|live|production/.test(status)) devStats[devName].done += 1;
      else if (/progress|dev|qa|review/.test(status)) devStats[devName].inProg += 1;
      else devStats[devName].todo += 1;
    });

    // Add bug data from QA report
    if (qaData?.data && Array.isArray(qaData.data)) {
      qaData.data.forEach(t => {
        const devs = t.developer_names || t.developers || '';
        const devName = typeof devs === 'string' ? devs.split(',')[0]?.trim() : (devs[0]?.name || devs[0] || '');
        if (devName && devStats[devName]) {
          devStats[devName].bugsRaised += (t.bug_count || 0);
          devStats[devName].bugsResolved += (t.bug_completed_count || 0);
        }
      });
    }

    // Calculate scores
    const results = Object.entries(devStats).map(([name, s]) => {
      const completionRate = s.tickets > 0 ? Math.round((s.done / s.tickets) * 100) : 0;
      const bugRate = s.tickets > 0 ? Math.round((s.bugsRaised / s.tickets) * 100) : 0;
      const efficiency = s.estHrs > 0 && s.devHrs >= 1 ? Math.min(200, Math.round((s.estHrs / s.devHrs) * 100)) : (s.devHrs < 1 ? 0 : 100);
      // Score: completion (40%) + low bugs (30%) + efficiency (30%)
      const score = Math.min(100, Math.max(0,
        Math.round(completionRate * 0.4 + Math.max(0, 100 - bugRate * 2) * 0.3 + Math.min(efficiency, 120) * 0.3)
      ));
      return { name, ...s, completionRate, bugRate, efficiency, score };
    });

    results.sort((a, b) => b.score - a.score);
    return results;
  }, [storyAnalysisData, qaData]);

  /* ───── Team members ───── */
  const teamMembers = useMemo(() => {
    if (!sprint?.team_member_str) return [];
    return sprint.team_member_str.split(',').map(n => n.trim()).filter(Boolean);
  }, [sprint]);

  /* ═══════════════ RENDER ═══════════════ */
  if (loading) {
    return (
      <div className="dash">
        <div className="dash-loading">
          <div className="dash-loading__spinner" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  if (!sprintsList.length) {
    return (
      <div className="dash">
        <div className="dash-empty">
          <FiActivity size={40} />
          <h3>No Ongoing Sprints</h3>
          <p>There are no active sprints to display. Create a sprint to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* ── Top Bar: Sprint Selector ── */}
      <div className="dash-topbar">
        <div className="dash-topbar__left">
          <h1 className="dash-topbar__title">Sprint Analytics</h1>
          <FilterDropdown label="Sprint" value={String(selectedSprintIdx)} options={sprintsList.map((s, i) => ({ id: String(i), name: s.sprint_name }))} onChange={v => setSelectedSprintIdx(Number(v))} renderLabel={o => o?.name || ''} getValue={o => o?.id} />
          {sprint && (
            <span className="dash-topbar__dates">
              <FiClock size={12} /> {formatDate(sprint.start_date)} — {formatDate(sprint.end_date)}
            </span>
          )}
        </div>
        <button className={`dash-refresh-btn ${refreshing ? 'dash-refresh-btn--spin' : ''}`} onClick={handleRefresh} title="Refresh data">
          <FiRefreshCw size={14} />
        </button>
      </div>

      {sprint && metrics && (
        <>
          {/* ── KPI Cards Row ── */}
          <div className="dash-kpi-row">
            <div className="dash-kpi">
              <div className="dash-kpi__icon dash-kpi__icon--purple"><FiTarget size={18} /></div>
              <div className="dash-kpi__body">
                <span className="dash-kpi__value">{metrics.total}</span>
                <span className="dash-kpi__label">Total Tickets</span>
              </div>
            </div>
            <div className="dash-kpi">
              <div className="dash-kpi__icon dash-kpi__icon--green"><FiCheckCircle size={18} /></div>
              <div className="dash-kpi__body">
                <span className="dash-kpi__value">{metrics.done}</span>
                <span className="dash-kpi__label">Completed</span>
              </div>
            </div>
            <div className="dash-kpi">
              <div className="dash-kpi__icon dash-kpi__icon--blue"><FiZap size={18} /></div>
              <div className="dash-kpi__body">
                <span className="dash-kpi__value">{metrics.inProg}</span>
                <span className="dash-kpi__label">In Progress</span>
              </div>
            </div>
            <div className="dash-kpi">
              <div className="dash-kpi__icon dash-kpi__icon--orange"><FiClock size={18} /></div>
              <div className="dash-kpi__body">
                <span className="dash-kpi__value">{metrics.todo}</span>
                <span className="dash-kpi__label">To Do</span>
              </div>
            </div>
            <div className="dash-kpi">
              <div className="dash-kpi__icon dash-kpi__icon--red"><GoBug size={18} /></div>
              <div className="dash-kpi__body">
                <span className="dash-kpi__value">{metrics.bugTickets}</span>
                <span className="dash-kpi__label">Bugs</span>
              </div>
            </div>
            <div className="dash-kpi">
              <div className="dash-kpi__icon dash-kpi__icon--red"><GoBug size={18} /></div>
              <div className="dash-kpi__body">
                <span className="dash-kpi__value">{metrics.bugCount}</span>
                <span className="dash-kpi__label">QA Failures</span>
              </div>
            </div>
          </div>

          {/* ── Row 2: Health Gauge + Status Donut + Completion ── */}
          <div className="dash-grid dash-grid--3">
            {/* Health Score */}
            <div className="dash-card">
              <div className="dash-card__header">
                <h3><FiActivity size={14} /> Sprint Health</h3>
              </div>
              <div className="dash-card__body dash-card__body--center">
                <Gauge
                  value={metrics.healthScore}
                  startAngle={-110}
                  endAngle={110}
                  width={180}
                  height={140}
                  sx={{
                    [`& .${gaugeClasses.valueText}`]: { fontSize: 28, fontWeight: 700, fill: 'var(--text-primary)' },
                    [`& .${gaugeClasses.valueArc}`]: { fill: metrics.healthScore >= 70 ? COLORS.green : metrics.healthScore >= 40 ? COLORS.orange : COLORS.red },
                    [`& .${gaugeClasses.referenceArc}`]: { fill: 'var(--border-subtle)' },
                  }}
                  text={({ value }) => `${value}`}
                />
                <span className={`dash-health-label ${metrics.healthScore >= 70 ? 'dash-health-label--good' : metrics.healthScore >= 40 ? 'dash-health-label--mid' : 'dash-health-label--bad'}`}>
                  {metrics.healthScore >= 70 ? 'Healthy' : metrics.healthScore >= 40 ? 'Needs Attention' : 'Critical'}
                </span>
              </div>
            </div>

            {/* Status Distribution Donut */}
            <div className="dash-card">
              <div className="dash-card__header">
                <h3><FiBarChart2Icon /> Status Distribution</h3>
              </div>
              <div className="dash-card__body dash-card__body--center">
                {pieData.length > 0 ? (
                  <PieChart
                    series={[{
                      data: pieData,
                      innerRadius: 45,
                      outerRadius: 75,
                      paddingAngle: 2,
                      cornerRadius: 3,
                    }]}
                    width={320}
                    height={180}
                    slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' }, itemMarkWidth: 8, itemMarkHeight: 8, labelStyle: { fontSize: 11, fill: 'var(--text-muted)' } } }}
                  />
                ) : (
                  <span className="dash-no-data">No data</span>
                )}
              </div>
            </div>

            {/* Completion Ring */}
            <div className="dash-card">
              <div className="dash-card__header">
                <h3><FiTrendingUp size={14} /> Completion</h3>
              </div>
              <div className="dash-card__body dash-card__body--center">
                <div className="dash-ring">
                  <svg viewBox="0 0 100 100" className="dash-ring__svg">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="var(--border-subtle)" strokeWidth="8" />
                    <circle
                      cx="50" cy="50" r="42" fill="none"
                      stroke={metrics.completionPct >= 70 ? COLORS.green : metrics.completionPct >= 40 ? COLORS.orange : COLORS.red}
                      strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={`${metrics.completionPct * 2.639} ${263.9 - metrics.completionPct * 2.639}`}
                      transform="rotate(-90 50 50)"
                      style={{ transition: 'stroke-dasharray 0.6s ease' }}
                    />
                  </svg>
                  <div className="dash-ring__text">
                    <span className="dash-ring__pct">{metrics.completionPct}%</span>
                    <span className="dash-ring__label">Done</span>
                  </div>
                </div>
                <div className="dash-completion-breakdown">
                  <span><span className="dash-dot dash-dot--green" /> {metrics.done} done</span>
                  <span><span className="dash-dot dash-dot--blue" /> {metrics.inProg} in progress</span>
                  <span><span className="dash-dot dash-dot--orange" /> {metrics.todo} to do</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Row 3: Insights — What's Working / What's Not ── */}
          <div className="dash-grid dash-grid--2">
            <div className="dash-card dash-card--good">
              <div className="dash-card__header">
                <h3><FiThumbsUp size={14} /> What's Working</h3>
              </div>
              <div className="dash-card__body">
                <ul className="dash-insights-list">
                  {insights.good.map((msg, i) => (
                    <li key={i} className="dash-insight dash-insight--good">
                      <FiCheckCircle size={13} className="dash-insight__icon" />
                      <span>{msg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="dash-card dash-card--bad">
              <div className="dash-card__header">
                <h3><FiThumbsDown size={14} /> Anomalies & Risks</h3>
              </div>
              <div className="dash-card__body">
                <ul className="dash-insights-list">
                  {insights.bad.map((msg, i) => (
                    <li key={i} className="dash-insight dash-insight--bad">
                      <FiAlertTriangle size={13} className="dash-insight__icon" />
                      <span>{msg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* ── Row 4: Status Breakdown Bar + Developer Workload ── */}
          <div className="dash-grid dash-grid--2">
            {/* Status bar chart */}
            <div className="dash-card">
              <div className="dash-card__header">
                <h3><FiBarChart2Icon /> Status Breakdown</h3>
              </div>
              <div className="dash-card__body dash-card__body--chart">
                {metrics.entries.length > 0 ? (
                  <BarChart
                    xAxis={[{ id: 'status', data: metrics.entries.map(([k]) => k), scaleType: 'band' }]}
                    series={[{
                      data: metrics.entries.map(([, v]) => v),
                      color: COLORS.purple,
                    }]}
                    height={240}
                    margin={{ left: 40, right: 16, top: 16, bottom: 50 }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={{
                      '& .MuiChartsAxis-tickLabel': { fill: 'var(--text-muted)', fontSize: '0.65rem' },
                      '& .MuiChartsAxis-line': { stroke: 'var(--border-subtle)' },
                      '& .MuiChartsAxis-tick': { stroke: 'var(--border-subtle)' },
                    }}
                  />
                ) : <span className="dash-no-data">No data</span>}
              </div>
            </div>

            {/* Developer workload */}
            <div className="dash-card">
              <div className="dash-card__header">
                <h3><FiUsers size={14} /> Developer Workload</h3>
              </div>
              <div className="dash-card__body dash-card__body--chart">
                {devWorkload ? (
                  <BarChart
                    xAxis={[{ id: 'dev', data: devWorkload.names, scaleType: 'band' }]}
                    series={[
                      { data: devWorkload.tickets, label: 'Tickets', color: COLORS.purple },
                      { data: devWorkload.devHrs, label: 'Dev Hours', color: COLORS.blue },
                      { data: devWorkload.qaHrs, label: 'QA Hours', color: COLORS.teal },
                    ]}
                    height={240}
                    margin={{ left: 40, right: 16, top: 16, bottom: 50 }}
                    slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'right' }, itemMarkWidth: 8, itemMarkHeight: 8, labelStyle: { fontSize: 10, fill: 'var(--text-muted)' } } }}
                    sx={{
                      '& .MuiChartsAxis-tickLabel': { fill: 'var(--text-muted)', fontSize: '0.65rem' },
                      '& .MuiChartsAxis-line': { stroke: 'var(--border-subtle)' },
                      '& .MuiChartsAxis-tick': { stroke: 'var(--border-subtle)' },
                    }}
                  />
                ) : <span className="dash-no-data">Loading analysis data...</span>}
              </div>
            </div>
          </div>

          {/* ── Row 5: QA Analysis + Team Info ── */}
          <div className="dash-grid dash-grid--2">
            {/* QA Analysis */}
            <div className="dash-card">
              <div className="dash-card__header">
                <h3><GoBug size={14} /> QA Analysis</h3>
              </div>
              <div className="dash-card__body">
                {qaSummary ? (
                  <div className="dash-qa-grid">
                    <div className="dash-qa-stat">
                      <span className="dash-qa-stat__value">{qaSummary.stories}</span>
                      <span className="dash-qa-stat__label">Stories</span>
                    </div>
                    <div className="dash-qa-stat">
                      <span className="dash-qa-stat__value">{qaSummary.totalBugs}</span>
                      <span className="dash-qa-stat__label">Total Bugs</span>
                    </div>
                    <div className="dash-qa-stat dash-qa-stat--green">
                      <span className="dash-qa-stat__value">{qaSummary.completedBugs}</span>
                      <span className="dash-qa-stat__label">Resolved</span>
                    </div>
                    <div className="dash-qa-stat dash-qa-stat--red">
                      <span className="dash-qa-stat__value">{qaSummary.pendingBugs}</span>
                      <span className="dash-qa-stat__label">Pending</span>
                    </div>
                    <div className="dash-qa-pass-bar">
                      <div className="dash-qa-pass-bar__track">
                        <div className="dash-qa-pass-bar__fill" style={{ width: `${qaSummary.passRate}%` }} />
                      </div>
                      <span className="dash-qa-pass-bar__label">{qaSummary.passRate}% Bug Resolution Rate</span>
                    </div>
                  </div>
                ) : <span className="dash-no-data">Loading QA data...</span>}
              </div>
            </div>

            {/* Team Info */}
            <div className="dash-card">
              <div className="dash-card__header">
                <h3><FiUsers size={14} /> Team — {sprint.team_name || 'Unknown'}</h3>
              </div>
              <div className="dash-card__body">
                <div className="dash-team-grid">
                  {teamMembers.map((name, i) => {
                    const initial = name[0]?.toUpperCase() || '?';
                    const avatarColors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b','#00acc1'];
                    const bg = avatarColors[initial.charCodeAt(0) % avatarColors.length];
                    return (
                      <div key={i} className="dash-team-member">
                        <span className="dash-team-avatar" style={{ background: bg }}>{initial}</span>
                        <span className="dash-team-name">{name}</span>
                      </div>
                    );
                  })}
                  {teamMembers.length === 0 && <span className="dash-no-data">No team members</span>}
                </div>
              </div>
            </div>
          </div>

          {/* ── Row 6: Developer Performance Scoreboard ── */}
          {devPerformance && devPerformance.length > 0 && (
            <div className="dash-card dash-card--full">
              <div className="dash-card__header">
                <h3><FiTrendingUp size={14} /> Developer Performance</h3>
              </div>
              <div className="dash-card__body">
                <div className="dash-perf-board">
                  {devPerformance.map((dev, idx) => {
                    const avatarColors = ['#7c5cfc','#1976d2','#e53935','#43a047','#f5a623','#8e24aa','#00897b','#00acc1'];
                    const bg = avatarColors[(dev.name || '?').charCodeAt(0) % avatarColors.length];
                    const scoreColor = dev.score >= 75 ? '#4caf50' : dev.score >= 50 ? '#ff9800' : '#f44336';
                    const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;

                    return (
                      <div key={dev.name} className={`dash-perf-card ${idx === 0 ? 'dash-perf-card--top' : ''}`}>
                        {/* Rank + Avatar */}
                        <div className="dash-perf-card__rank">
                          <span className="dash-perf-rank">{medal || `#${idx + 1}`}</span>
                          <span className="dash-perf-avatar" style={{ background: bg }}>{(dev.name || '?')[0].toUpperCase()}</span>
                        </div>

                        {/* Info */}
                        <div className="dash-perf-card__info">
                          <span className="dash-perf-name">{dev.name}</span>
                          <div className="dash-perf-stats">
                            <span className="dash-perf-stat"><strong>{dev.tickets}</strong> tickets</span>
                            <span className="dash-perf-stat dash-perf-stat--green"><strong>{dev.done}</strong> done</span>
                            <span className="dash-perf-stat dash-perf-stat--blue"><strong>{dev.inProg}</strong> in progress</span>
                            {dev.bugsRaised > 0 && <span className="dash-perf-stat dash-perf-stat--red"><strong>{dev.bugsRaised}</strong> bugs</span>}
                          </div>
                        </div>

                        {/* Metrics */}
                        <div className="dash-perf-card__metrics">
                          <div className="dash-perf-metric">
                            <span className="dash-perf-metric__label">Completion</span>
                            <div className="dash-perf-metric__bar"><div style={{ width: `${dev.completionRate}%`, background: '#4caf50' }} /></div>
                            <span className="dash-perf-metric__val">{dev.completionRate}%</span>
                          </div>
                          <div className="dash-perf-metric">
                            <span className="dash-perf-metric__label">Efficiency</span>
                            <div className="dash-perf-metric__bar"><div style={{ width: `${Math.min(dev.efficiency, 100)}%`, background: '#2196f3' }} /></div>
                            <span className="dash-perf-metric__val">{dev.efficiency}%</span>
                          </div>
                          {dev.devHrs > 0 && (
                            <div className="dash-perf-metric">
                              <span className="dash-perf-metric__label">Dev Time</span>
                              <span className="dash-perf-metric__val" style={{ marginLeft: 'auto' }}>{dev.devHrs.toFixed(1)}h</span>
                            </div>
                          )}
                        </div>

                        {/* Score */}
                        <div className="dash-perf-card__score">
                          <svg viewBox="0 0 40 40" className="dash-perf-ring">
                            <circle cx="20" cy="20" r="16" fill="none" stroke="var(--border-subtle)" strokeWidth="3" />
                            <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="3" strokeLinecap="round"
                              strokeDasharray={`${dev.score * 1.005} ${100.5 - dev.score * 1.005}`} transform="rotate(-90 20 20)" />
                          </svg>
                          <span className="dash-perf-score-val" style={{ color: scoreColor }}>{dev.score}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* Simple icon wrapper to avoid import conflict */
const FiBarChart2Icon = () => <FiActivity size={14} />;

export default HomePage;
